import React, { createContext, useState, useContext, useEffect } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const AuthContext = createContext();

export const useAuth = () => {
  return useContext(AuthContext);
};

export const AuthProvider = ({ children }) => {
  const initialToken = localStorage.getItem('token');
  const initialUser = localStorage.getItem('user');

  if (initialToken) {
    axios.defaults.headers.common['Authorization'] = `Bearer ${initialToken}`;
  }

  const [user, setUser] = useState(initialUser ? JSON.parse(initialUser) : null);
  const [token, setToken] = useState(initialToken);
  const [loading, setLoading] = useState(true);

  axios.defaults.baseURL = API_URL;

  // Set auth header on all requests
  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
      delete axios.defaults.headers.common['Authorization'];
    }
  }, [token]);

  // Auto-logout when the token is expired/invalid
  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        const status = error.response?.status;
        const message = String(error.response?.data?.error || '');
        const isAuthFailure =
          status === 401 && /token/i.test(message);

        if (isAuthFailure && localStorage.getItem('token')) {
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          setToken(null);
          setUser(null);
          if (window.location.pathname !== '/login') {
            window.location.href = '/login';
          }
        }
        return Promise.reject(error);
      }
    );
    return () => axios.interceptors.response.eject(interceptor);
  }, []);

  // Check if user is logged in on mount
  useEffect(() => {
    const storedToken = localStorage.getItem('token');
    const storedUser = localStorage.getItem('user');
    
    if (storedToken && storedUser) {
      setToken(storedToken);
      setUser(JSON.parse(storedUser));
    }
    setLoading(false);
  }, []);

  const login = async (email, password, recaptchaToken) => {
    try {
      const response = await axios.post(`${API_URL}/auth/login`, {
        email,
        password,
        recaptchaToken,
      });
      const { token: newToken, user: userData } = response.data;
      
      localStorage.setItem('token', newToken);
      localStorage.setItem('user', JSON.stringify(userData));
      
      setToken(newToken);
      setUser(userData);
      
      return { success: true, user: userData };
    } catch (error) {
      const apiError = error.response?.data?.error;
      const networkHint =
        !error.response && error.message
          ? `Cannot reach API at ${API_URL}. Is the server running on port 5000?`
          : null;
      return {
        success: false,
        error: apiError || networkHint || 'Login failed. Please try again.',
        needsVerification: error.response?.data?.needsVerification,
      };
    }
  };

  const signupCompanysAdmin = async (userData) => {
    try {
      const response = await axios.post(`${API_URL}/auth/signup/company-admin`, userData);
      return {
        success: true,
        message: response.data.message,
        autoVerified: response.data.autoVerified,
        needsVerification: response.data.needsVerification,
        email: response.data.email,
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || 'Signup failed. Please try again.',
        needsVerification: error.response?.data?.needsVerification,
        email: error.response?.data?.email,
      };
    }
  };

  const resendVerificationEmail = async (email) => {
    try {
      const response = await axios.post(`${API_URL}/auth/resend-verification`, { email });
      return { success: true, message: response.data.message };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to resend verification email.',
      };
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setToken(null);
    setUser(null);
  };

  const updateUser = (updates) => {
    setUser((prev) => {
      if (!prev) return prev;
      const next = { ...prev, ...updates };
      localStorage.setItem('user', JSON.stringify(next));
      return next;
    });
  };

  const value = {
    user,
    token,
    loading,
    login,
    signupCompanysAdmin,
    resendVerificationEmail,
    logout,
    updateUser,
    isAuthenticated: !!token,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
